import React, { useState, useEffect, useCallback } from 'react';
import { purchasesBillsApi, type BillDto } from '../../services/api';
import RecordBillModal from './RecordBillModal';

function formatPeso(amount: number): string {
  return new Intl.NumberFormat('en-PH', {
    style: 'currency',
    currency: 'PHP',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);
}

function formatDate(iso: string): string {
  const [y, m, d] = iso.split('-');
  return `${d}/${m}/${y}`;
}

export default function BillsPage() {
  const [bills, setBills] = useState<BillDto[]>([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);

  const fetchBills = useCallback(async () => {
    setLoading(true);
    try {
      const res = await purchasesBillsApi.list(search || undefined);
      setBills(res.data ?? []);
    } catch {
      setBills([]);
    } finally {
      setLoading(false);
    }
  }, [search]);

  useEffect(() => {
    fetchBills();
  }, [fetchBills]);

  return (
    <div className="max-w-7xl mx-auto w-full flex-1 min-h-0 flex flex-col overflow-hidden">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4 shrink-0">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Purchase Bills</h1>
          <p className="text-sm text-gray-500 mt-0.5">Manage vendor bills and expenses</p>
        </div>
        <button
          type="button"
          onClick={() => setModalOpen(true)}
          className="inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-brand-600 text-white text-sm font-semibold hover:bg-brand-700 focus:ring-2 focus:ring-brand-500 focus:ring-offset-2 transition-colors shrink-0"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          New Bill
        </button>
      </div>

      <div className="mb-4 shrink-0">
        <div className="relative max-w-md">
          <svg
            className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
          <input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search bills..."
            className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-gray-200 bg-white text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500 outline-none text-sm"
          />
        </div>
      </div>

      <div className="flex-1 min-h-0 overflow-auto bg-white rounded-xl shadow-card border border-gray-100">
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <div className="text-gray-500 text-sm">Loading bills...</div>
          </div>
        ) : bills.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 text-center text-gray-500">
            <svg
              className="w-12 h-12 text-gray-300 mb-3"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
              />
            </svg>
            <p className="font-medium text-gray-600">No bills found.</p>
            <p className="text-sm mt-1">Record your first expense.</p>
            <button
              type="button"
              onClick={() => setModalOpen(true)}
              className="mt-4 inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-brand-600 text-white text-sm font-medium hover:bg-brand-700"
            >
              + New Bill
            </button>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[700px]">
              <thead>
                <tr className="border-b border-gray-200 bg-gray-50/80">
                  <th className="text-left text-xs font-semibold text-gray-600 uppercase tracking-wider py-3 px-4">
                    Bill #
                  </th>
                  <th className="text-left text-xs font-semibold text-gray-600 uppercase tracking-wider py-3 px-4">
                    Vendor
                  </th>
                  <th className="text-left text-xs font-semibold text-gray-600 uppercase tracking-wider py-3 px-4">
                    Date
                  </th>
                  <th className="text-left text-xs font-semibold text-gray-600 uppercase tracking-wider py-3 px-4">
                    Due Date
                  </th>
                  <th className="text-left text-xs font-semibold text-gray-600 uppercase tracking-wider py-3 px-4">
                    Amount
                  </th>
                  <th className="text-left text-xs font-semibold text-gray-600 uppercase tracking-wider py-3 px-4">
                    Balance Due
                  </th>
                  <th className="text-left text-xs font-semibold text-gray-600 uppercase tracking-wider py-3 px-4">
                    Status
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {bills.map((b) => (
                  <tr key={b.id} className="hover:bg-gray-50/50 transition-colors">
                    <td className="py-3 px-4">
                      <span className="font-medium text-gray-900">{b.bill_number}</span>
                    </td>
                    <td className="py-3 px-4 text-sm text-gray-600">{b.vendor_name}</td>
                    <td className="py-3 px-4 text-sm text-gray-600">{formatDate(b.bill_date)}</td>
                    <td className="py-3 px-4 text-sm text-gray-600">{formatDate(b.due_date)}</td>
                    <td className="py-3 px-4 text-sm text-gray-900">
                      {formatPeso(b.total_amount)}
                    </td>
                    <td className="py-3 px-4">
                      <span
                        className={
                          b.balance_due > 0 ? 'text-red-600 font-medium' : 'text-gray-600'
                        }
                      >
                        {formatPeso(b.balance_due)}
                      </span>
                    </td>
                    <td className="py-3 px-4">
                      <span
                        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          b.status === 'paid'
                            ? 'bg-emerald-50 text-emerald-700'
                            : b.status === 'overdue'
                              ? 'bg-red-50 text-red-700'
                              : 'bg-amber-50 text-amber-700'
                        }`}
                      >
                        {b.status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <RecordBillModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        onSuccess={fetchBills}
      />
    </div>
  );
}
